import { ReactNode, createElement } from "react";
import { PopupConfig } from "./Popup.types";

/**
 * Normalise a path or URL for comparison: reduce full URLs to their pathname,
 * ensure a leading slash, drop any trailing slash and lowercase it.
 */
function normalizePath(value: string): string {
  let path = value || "/";

  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      // leave as-is if it can't be parsed
    }
  }

  if (!path.startsWith("/")) path = "/" + path;
  if (path.length > 1) path = path.replace(/\/+$/, "");

  return path.toLowerCase();
}

/**
 * Whether a targeting entry matches the current pathname. A trailing "/*"
 * matches the whole section (the base path and anything beneath it).
 */
function entryMatches(entry: string, pathname: string): boolean {
  const target = normalizePath(pathname);

  if (entry.endsWith("/*")) {
    const base = normalizePath(entry.slice(0, -2));
    return target === base || target.startsWith(base + "/");
  }

  return normalizePath(entry) === target;
}

/**
 * Whether a popup should be shown on the given pathname, based on its display
 * mode and targeted pages.
 */
export function popupMatchesPath(popup: PopupConfig, pathname: string): boolean {
  const mode = popup.displayMode || "all";
  if (mode === "all") return true;

  const hit = (popup.pages || []).some((entry) => entryMatches(entry, pathname));

  if (mode === "include") return hit;
  if (mode === "exclude") return !hit;

  return true;
}

/**
 * Parses a single paragraph's lightweight **bold** / *italic* markers into
 * React nodes, mirroring the wp-admin live preview. Intentionally not a full
 * markdown/HTML parser — just the two markers editors are told about.
 */
function parseInlineMarkers(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      nodes.push(
        createElement("strong", { key: `${keyPrefix}-${key++}` }, match[1])
      );
    } else if (match[2] !== undefined) {
      nodes.push(
        createElement("em", { key: `${keyPrefix}-${key++}` }, match[2])
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/**
 * Parse the popup description into paragraphs — a blank line (Enter twice)
 * starts a new one, a single Enter is a line break within the same
 * paragraph — mirroring the wp-admin live preview, applying the
 * **bold** / *italic* markers throughout.
 */
export function parsePopupDescription(
  text: string,
  className?: string
): ReactNode[] {
  return text
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0)
    .map((paragraph, i) => {
      const lines = paragraph.split("\n");
      const children: ReactNode[] = [];
      lines.forEach((line, j) => {
        if (j > 0) {
          children.push(createElement("br", { key: `p${i}-br${j}` }));
        }
        children.push(...parseInlineMarkers(line, `p${i}-l${j}`));
      });
      return createElement("p", { key: i, className }, ...children);
    });
}
